import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { WidgetConfig, CustomChartConfig, DataSource, DataSourceField } from "./types";

interface CustomChartBuilderProps {
  onAdd: (widget: WidgetConfig, size: { w: number; h: number }) => void;
  onClose: () => void;
  initialWidget?: WidgetConfig | null;
}

/* ------------------------------------------------------------------ */
/*  Data Sources                                                       */
/* ------------------------------------------------------------------ */

const DATA_SOURCES: DataSource[] = [
  {
    id: "eoc",
    label: "Calls (EOC)",
    fields: [
      { key: "cashCollected", label: "Cash Collected", type: "number" },
      { key: "revenueGenerated", label: "Revenue Generated", type: "number" },
      { key: "mrrNumber", label: "MRR", type: "number" },
      { key: "closerName", label: "Closer Name", type: "string" },
      { key: "setterName", label: "Setter Name", type: "string" },
      { key: "country", label: "Country", type: "string" },
      { key: "sourcePlatform", label: "Source Platform", type: "string" },
      { key: "callOutcome", label: "Call Outcome", type: "string" },
      { key: "offerPitched", label: "Offer Pitched", type: "string" },
      { key: "timestamp", label: "Timestamp", type: "date" },
      { key: "dateCallWasTaken", label: "Date Call Was Taken", type: "date" },
    ],
  },
  {
    id: "daily_analytics",
    label: "Website Analytics",
    fields: [
      { key: "sessions", label: "Sessions", type: "number" },
      { key: "newUsers", label: "New Users", type: "number" },
      { key: "totalUsers", label: "Total Users", type: "number" },
      { key: "bounceRate", label: "Bounce Rate", type: "number" },
      { key: "avgSessionDuration", label: "Avg Session Duration", type: "number" },
      { key: "date", label: "Date", type: "date" },
    ],
  },
  {
    id: "facebook_ads",
    label: "Facebook Ads",
    fields: [
      { key: "amountSpent", label: "Amount Spent", type: "number" },
      { key: "impressions", label: "Impressions", type: "number" },
      { key: "reach", label: "Reach", type: "number" },
      { key: "ctr", label: "CTR", type: "number" },
      { key: "costPerLead", label: "Cost Per Lead", type: "number" },
      { key: "leads", label: "Leads", type: "number" },
      { key: "date", label: "Date", type: "date" },
      { key: "campaignName", label: "Campaign Name", type: "string" },
    ],
  },
  {
    id: "expenses",
    label: "Expenses",
    fields: [
      { key: "amount", label: "Amount", type: "number" },
      { key: "category", label: "Category", type: "string" },
      { key: "date", label: "Date", type: "date" },
    ],
  },
  {
    id: "setter_eod",
    label: "Setter Reports",
    fields: [
      { key: "dials", label: "Dials", type: "number" },
      { key: "connected", label: "Connected", type: "number" },
      { key: "leadsBooked", label: "Leads Booked", type: "number" },
      { key: "setterName", label: "Setter Name", type: "string" },
      { key: "eodDate", label: "EOD Date", type: "date" },
    ],
  },
  {
    id: "closer_eod",
    label: "Closer Reports",
    fields: [
      { key: "callsOnCalendar", label: "Calls on Calendar", type: "number" },
      { key: "showedUp", label: "Showed Up", type: "number" },
      { key: "closes", label: "Closes", type: "number" },
      { key: "cashGenerated", label: "Cash Generated", type: "number" },
      { key: "closerName", label: "Closer Name", type: "string" },
      { key: "eodDate", label: "EOD Date", type: "date" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Chart type metadata                                                */
/* ------------------------------------------------------------------ */

type ChartType = CustomChartConfig["chartType"];

interface ChartTypeOption {
  type: ChartType;
  label: string;
  icon: React.ReactNode;
}

const CHART_TYPES: ChartTypeOption[] = [
  {
    type: "line",
    label: "Line",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 17l4-4 4 4 4-8 4 4" />
      </svg>
    ),
  },
  {
    type: "area",
    label: "Area",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24">
        <path d="M3 17l4-4 4 4 4-8 4 4v8H3z" fill="currentColor" opacity={0.15} />
        <path d="M3 17l4-4 4 4 4-8 4 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
      </svg>
    ),
  },
  {
    type: "bar",
    label: "Bar",
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24">
        <rect x="3" y="12" width="4" height="8" rx="1" fill="currentColor" opacity={0.3} />
        <rect x="10" y="6" width="4" height="14" rx="1" fill="currentColor" opacity={0.5} />
        <rect x="17" y="9" width="4" height="11" rx="1" fill="currentColor" opacity={0.7} />
      </svg>
    ),
  },
  {
    type: "pie",
    label: "Pie",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
  },
];

const AGGREGATION_OPTIONS: Array<{ value: CustomChartConfig["aggregation"]; label: string }> = [
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "count", label: "Count" },
  { value: "min", label: "Min" },
  { value: "max", label: "Max" },
];

/* ------------------------------------------------------------------ */
/*  Step transition variants                                           */
/* ------------------------------------------------------------------ */

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function fieldsByType(fields: DataSourceField[], type: DataSourceField["type"]) {
  return fields.filter((f) => f.type === type);
}

function fieldsForXAxis(fields: DataSourceField[]) {
  return fields.filter((f) => f.type === "date" || f.type === "string");
}

/* ------------------------------------------------------------------ */
/*  Data source icons (per collection id)                              */
/* ------------------------------------------------------------------ */

const DATA_SOURCE_ICONS: Record<string, React.ReactNode> = {
  eoc: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
  daily_analytics: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  facebook_ads: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  expenses: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  setter_eod: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  closer_eod: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const TOTAL_STEPS = 4;

export function CustomChartBuilder({ onAdd, onClose, initialWidget }: CustomChartBuilderProps) {
  const initialConfig = (initialWidget?.config.chartConfig as CustomChartConfig | undefined)
    ?? (initialWidget?.config as unknown as CustomChartConfig | undefined);
  /* ---- wizard state ---- */
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);

  /* ---- Step 1: data source ---- */
  const [selectedCollection, setSelectedCollection] = useState<string>(initialConfig?.collection ?? "");

  /* ---- Step 2: chart type ---- */
  const [selectedChartType, setSelectedChartType] = useState<ChartType | "">(initialConfig?.chartType ?? "");

  /* ---- Step 3: fields ---- */
  const [selectedGroupBy, setSelectedGroupBy] = useState(initialConfig?.groupBy ?? "");
  const [selectedMetric, setSelectedMetric] = useState(initialConfig?.metric ?? "");
  const [selectedAggregation, setSelectedAggregation] = useState<CustomChartConfig["aggregation"]>(initialConfig?.aggregation ?? "sum");
  const [limit, setLimit] = useState(initialConfig?.limit ?? 20);

  /* ---- Step 4: title ---- */
  const [title, setTitle] = useState(initialWidget?.title ?? "");

  /* ---- derived ---- */
  const selectedSource = useMemo(
    () => DATA_SOURCES.find((s) => s.id === selectedCollection) ?? null,
    [selectedCollection],
  );

  const isPie = selectedChartType === "pie";

  useEffect(() => {
    if (!initialWidget) return;
    setStep(4);
  }, [initialWidget]);

  const groupByOptions = useMemo(() => {
    if (!selectedSource) return [];
    return isPie ? fieldsByType(selectedSource.fields, "string") : fieldsForXAxis(selectedSource.fields);
  }, [selectedSource, isPie]);

  const metricOptions = useMemo(() => {
    if (!selectedSource) return [];
    return fieldsByType(selectedSource.fields, "number");
  }, [selectedSource]);

  /* ---- navigation helpers ---- */
  const canGoNext = (): boolean => {
    switch (step) {
      case 1:
        return selectedCollection !== "";
      case 2:
        return selectedChartType !== "";
      case 3:
        return selectedGroupBy !== "" && selectedMetric !== "";
      case 4:
        return title.trim() !== "";
      default:
        return false;
    }
  };

  const goNext = () => {
    if (!canGoNext()) return;
    setDirection(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  /* ---- reset downstream state when source or chart type changes ---- */
  const handleSelectCollection = (id: string) => {
    setSelectedCollection(id);
    setSelectedGroupBy("");
    setSelectedMetric("");
  };

  const handleSelectChartType = (type: ChartType) => {
    setSelectedChartType(type);
    setSelectedGroupBy("");
    setSelectedMetric("");
  };

  /* ---- submit ---- */
  const handleAdd = () => {
    if (!canGoNext()) return;

    const config: CustomChartConfig = {
      collection: selectedCollection,
      chartType: selectedChartType as ChartType,
      groupBy: selectedGroupBy,
      metric: selectedMetric,
      aggregation: selectedAggregation,
      limit,
    };

    const widget: WidgetConfig = {
      id: initialWidget?.id ?? `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "customChart",
      title: title.trim(),
      config: { chartConfig: config as unknown as Record<string, unknown> },
    };

    onAdd(widget, { w: 6, h: 8 });
  };

  /* ---- step labels for indicator ---- */
  const stepLabels = ["Data Source", "Chart Type", "Configure", "Name & Add"];

  /* ================================================================ */
  /*  Render                                                          */
  /* ================================================================ */

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{initialWidget ? "Edit Custom Chart" : "Create Custom Chart"}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ---- Step indicator ---- */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-center justify-between">
            {stepLabels.map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = stepNum === step;
              const isCompleted = stepNum < step;

              return (
                <div key={label} className="flex items-center flex-1 last:flex-initial">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                        isActive
                          ? "bg-[var(--brand-primary)] text-white"
                          : isCompleted
                            ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        stepNum
                      )}
                    </div>
                    <span
                      className={`text-[11px] font-medium whitespace-nowrap ${
                        isActive ? "text-[var(--brand-primary)]" : "text-muted-foreground"
                      }`}
                    >
                      {label}
                    </span>
                  </div>

                  {/* Connector line (not after last step) */}
                  {idx < stepLabels.length - 1 && (
                    <div className="flex-1 mx-2 mt-[-18px]">
                      <div
                        className={`h-0.5 rounded-full transition-colors ${
                          stepNum < step ? "bg-[var(--brand-primary)]/40" : "bg-border"
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- Step content (animated) ---- */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-[320px]">
          <AnimatePresence mode="wait" custom={direction}>
            {/* ======================== STEP 1 ======================== */}
            {step === 1 && (
              <motion.div
                key="step-1"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="space-y-3"
              >
                <p className="text-sm text-muted-foreground mb-4">
                  Choose a data source to build your chart from.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {DATA_SOURCES.map((source) => {
                    const isSelected = selectedCollection === source.id;
                    return (
                      <button
                        key={source.id}
                        onClick={() => handleSelectCollection(source.id)}
                        className={`relative flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 ring-1 ring-[var(--brand-primary)]/30"
                            : "border-border hover:border-[var(--brand-primary)]/40 hover:bg-muted/50"
                        }`}
                      >
                        <div
                          className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                            isSelected
                              ? "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {DATA_SOURCE_ICONS[source.id]}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${isSelected ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
                            {source.label}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {source.fields.length} fields
                          </p>
                        </div>
                        {isSelected && (
                          <motion.div
                            layoutId="source-check"
                            className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--brand-primary)] flex items-center justify-center"
                          >
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </motion.div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* ======================== STEP 2 ======================== */}
            {step === 2 && (
              <motion.div
                key="step-2"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="space-y-3"
              >
                <p className="text-sm text-muted-foreground mb-4">
                  Select the chart type that best represents your data.
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {CHART_TYPES.map((ct) => {
                    const isSelected = selectedChartType === ct.type;
                    return (
                      <button
                        key={ct.type}
                        onClick={() => handleSelectChartType(ct.type)}
                        className={`flex flex-col items-center gap-2 p-5 rounded-xl border transition-all ${
                          isSelected
                            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5 ring-1 ring-[var(--brand-primary)]/30"
                            : "border-border hover:border-[var(--brand-primary)]/40 hover:bg-muted/50"
                        }`}
                      >
                        <div
                          className={`transition-colors ${
                            isSelected ? "text-[var(--brand-primary)]" : "text-muted-foreground"
                          }`}
                        >
                          {ct.icon}
                        </div>
                        <span
                          className={`text-sm font-medium ${
                            isSelected ? "text-[var(--brand-primary)]" : "text-foreground"
                          }`}
                        >
                          {ct.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* ======================== STEP 3 ======================== */}
            {step === 3 && (
              <motion.div
                key="step-3"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="space-y-5"
              >
                <p className="text-sm text-muted-foreground mb-2">
                  Configure the fields and aggregation for your chart.
                </p>

                {/* Group-by / X-axis / Category field */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {isPie ? "Category Field" : "X-Axis"}
                    <span className="text-xs text-muted-foreground ml-1">
                      ({isPie ? "string" : "date or string"})
                    </span>
                  </label>
                  <select
                    value={selectedGroupBy}
                    onChange={(e) => setSelectedGroupBy(e.target.value)}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]/50"
                  >
                    <option value="">Select a field...</option>
                    {groupByOptions.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label} ({f.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Metric / Y-axis / Value field */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {isPie ? "Value Field" : "Y-Axis"}
                    <span className="text-xs text-muted-foreground ml-1">(number)</span>
                  </label>
                  <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]/50"
                  >
                    <option value="">Select a field...</option>
                    {metricOptions.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Aggregation */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Aggregation</label>
                  <div className="flex flex-wrap gap-2">
                    {AGGREGATION_OPTIONS.map((agg) => {
                      const isActive = selectedAggregation === agg.value;
                      return (
                        <button
                          key={agg.value}
                          onClick={() => setSelectedAggregation(agg.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                            isActive
                              ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                              : "border-border text-muted-foreground hover:border-[var(--brand-primary)]/40"
                          }`}
                        >
                          {agg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Limit */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    Limit
                    <span className="text-xs text-muted-foreground ml-1">(max data points)</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={limit}
                    onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value, 10) || 20))}
                    className="w-32 rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]/50"
                  />
                </div>
              </motion.div>
            )}

            {/* ======================== STEP 4 ======================== */}
            {step === 4 && (
              <motion.div
                key="step-4"
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="space-y-6"
              >
                <p className="text-sm text-muted-foreground mb-2">
                  Give your chart a name and review the configuration.
                </p>

                {/* Title input */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Chart Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Revenue Over Time"
                    autoFocus
                    className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]/50"
                  />
                </div>

                {/* Summary card */}
                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
                  <h4 className="font-medium text-foreground mb-3">Configuration Summary</h4>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                    <span className="text-muted-foreground">Data Source</span>
                    <span className="font-medium text-foreground">{selectedSource?.label}</span>

                    <span className="text-muted-foreground">Chart Type</span>
                    <span className="font-medium text-foreground capitalize">{selectedChartType}</span>

                    <span className="text-muted-foreground">{isPie ? "Category" : "X-Axis"}</span>
                    <span className="font-medium text-foreground">
                      {selectedSource?.fields.find((f) => f.key === selectedGroupBy)?.label ?? selectedGroupBy}
                    </span>

                    <span className="text-muted-foreground">{isPie ? "Value" : "Y-Axis"}</span>
                    <span className="font-medium text-foreground">
                      {selectedSource?.fields.find((f) => f.key === selectedMetric)?.label ?? selectedMetric}
                    </span>

                    <span className="text-muted-foreground">Aggregation</span>
                    <span className="font-medium text-foreground capitalize">{selectedAggregation}</span>

                    <span className="text-muted-foreground">Limit</span>
                    <span className="font-medium text-foreground">{limit}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ---- Footer: Back / Next / Add ---- */}
        <div className="flex items-center justify-between p-5 border-t border-border">
          <button
            onClick={step === 1 ? onClose : goBack}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>

          {step < TOTAL_STEPS ? (
            <button
              onClick={goNext}
              disabled={!canGoNext()}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] hover:shadow-lg hover:shadow-[var(--brand-primary)]/20"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleAdd}
              disabled={!canGoNext()}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] hover:shadow-lg hover:shadow-[var(--brand-primary)]/20"
            >
              Add Chart
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
